import React, { useState, useRef, useEffect } from "react";
import { Sparkles, Send, X, Loader2, MessageCircle } from "lucide-react";
import api from "@/lib/api";

const SUGGESTIONS = {
  dashboard: ["What should I prioritize today?", "Summarize service activity this week", "Where are bottlenecks?"],
  services: ["Best workflow for carwash queues?", "Reduce average detail turnaround", "Prioritization tips"],
  carwash: ["Optimal cycle time per vehicle", "Staffing per bay", "Quality checklist"],
  used_car_detail: ["Used car detail checklist", "Buff & polish workflow", "Lot prep timing"],
  porter: ["Reduce porter idle time", "Lot organization tips", "Trip batching"],
  bodyshop: ["Faster estimate writing", "Parts ordering checklist", "Cycle time KPIs"],
  onboarding: ["New hire day-1 checklist", "Documents to e-sign", "Training milestones"],
  employees: ["Build a 30-day onboarding plan", "Required signatures", "Performance check-ins"],
  dealerships: ["Upsell carwash + detail bundle", "Retention talking points", "Onboarding new dealership"],
  signatures: ["Standard onboarding doc set", "Compliance reminders", "Reminder cadence"],
};

export default function AICopilot({ section = "dashboard" }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    setMessages([]);
    setSessionId(null);
  }, [section]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || loading) return;
    setMessages((m) => [...m, { role: "user", text: message }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { section, message, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "AI temporarily unavailable. Try again in a moment." }]);
    } finally {
      setLoading(false);
    }
  };

  const suggestions = SUGGESTIONS[section] || SUGGESTIONS.dashboard;

  return (
    <>
      {/* Trigger button */}
      <button
        data-testid="ai-copilot-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-40 bg-[#002FA7] text-white p-4 hover:bg-[#00227A] shadow-lg flex items-center gap-2"
        aria-label="Toggle EAG AI Copilot"
      >
        <Sparkles size={18} />
        <span className="label-caps">AI Copilot</span>
      </button>

      {/* Slide-over panel */}
      <div
        className={`fixed inset-y-0 right-0 z-50 w-full sm:w-[420px] bg-white border-l border-zinc-200 transform transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="ai-copilot-panel"
      >
        <div className="flex items-center justify-between p-5 border-b border-zinc-200 bg-black text-white">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-[#FF2B00]" />
            <div>
              <div className="font-display text-lg leading-none">EAG Copilot</div>
              <div className="label-caps text-zinc-400 mt-1">{section.replace("_", " ")}</div>
            </div>
          </div>
          <button data-testid="ai-copilot-close" onClick={() => setOpen(false)} className="hover:text-[#FF2B00]">
            <X size={20} />
          </button>
        </div>

        <div ref={scrollRef} className="overflow-y-auto p-5 space-y-4" style={{ height: "calc(100% - 200px)" }}>
          {messages.length === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-600">
                Hi — I'm your context-aware copilot. Ask me anything about{" "}
                <span className="font-semibold text-black">{section.replace("_", " ")}</span>.
              </p>
              <div className="flex flex-col gap-2">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`ai-suggestion-${i}`}
                    onClick={() => send(s)}
                    className="text-left px-3 py-2 border border-zinc-200 hover:border-black hover:bg-zinc-50 text-sm"
                  >
                    <MessageCircle size={12} className="inline mr-2 text-[#002FA7]" />
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
              <div
                className={`inline-block max-w-[90%] px-3 py-2 text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "bg-black text-white" : "bg-zinc-100 text-black border border-zinc-200"
                }`}
              >
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="text-left">
              <div className="inline-flex items-center gap-2 px-3 py-2 bg-zinc-100 text-sm">
                <Loader2 size={14} className="animate-spin" /> Thinking…
              </div>
            </div>
          )}
        </div>

        <div className="absolute bottom-0 left-0 right-0 border-t border-zinc-200 bg-white p-4">
          <div className="flex items-center gap-2">
            <input
              data-testid="ai-copilot-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask the copilot…"
              className="flex-1 border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-black"
            />
            <button
              data-testid="ai-copilot-send"
              onClick={() => send()}
              disabled={loading || !input.trim()}
              className="bg-[#002FA7] text-white px-3 py-2 hover:bg-[#00227A] disabled:opacity-50"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
